import { useTranslation } from "react-i18next";
import { displayTicker } from "../tickers";

interface StrategyTickersStrategy {
  chartedSymbol?: string | null;
  bullSymbol?: string | null;
  bearSymbol?: string | null;
}

/**
 * What a strategy trades: what it charts, then the bull and bear vehicles where it trades a pair.
 *
 * Split out of the identity band because the checkout order review needs the same three tickers in a
 * table cell, where the band's strip has nothing to sit across.
 *
 * Truncates rather than wraps. In the band it shares a line with the marks and is the side that can
 * grow - a long ticker with both legs is the widest case - so it gives way rather than pushing the
 * marks off the name they describe.
 */
export function StrategyTickers({
  strategy,
}: {
  strategy: StrategyTickersStrategy;
}) {
  const { t } = useTranslation("tradingStrategies");

  const underlying = displayTicker(strategy.chartedSymbol);

  return (
    <div className="d-flex align-items-center gap-2 text-truncate">
      {underlying && (
        <span
          className="font-monospace fw-medium text-truncate"
          title={t("traits.underlying", { ticker: underlying })}
        >
          {underlying}
        </span>
      )}
      {(strategy.bullSymbol || strategy.bearSymbol) && (
        <>
          <span className="strategy-band-sep" aria-hidden />
          {strategy.bullSymbol && (
            <span
              className="font-monospace small"
              style={{ color: "var(--sl-positive)" }}
            >
              {displayTicker(strategy.bullSymbol)}
            </span>
          )}
          {strategy.bearSymbol && (
            <span
              className="font-monospace small"
              style={{ color: "var(--sl-negative)" }}
            >
              {displayTicker(strategy.bearSymbol)}
            </span>
          )}
        </>
      )}
    </div>
  );
}
